(() => {
  if (window.AknesCarouselCountdownManager) {
    window.AknesCarouselCountdownManager.initAll();
    return;
  }

  const instances = new WeakMap();
  const stateClasses = [
    'aknes-hero-slide--upcoming',
    'aknes-hero-slide--live',
    'aknes-hero-slide--ending',
    'aknes-hero-slide--ended'
  ];
  const isoWithTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;

  class CountdownSection {
    constructor(root) {
      this.root = root;
      this.countdowns = Array.from(root.querySelectorAll('[data-aknes-countdown]'));
      this.timer = null;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      if (this.countdowns.length === 0) return;
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.update();
      this.start();
    }

    start() {
      if (this.timer || document.hidden || this.countdowns.length === 0) return;
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.countdowns = [];
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stop();
        return;
      }

      this.update();
      this.start();
    }

    warnInvalid(element) {
      if (this.root.dataset.aknesCountdownDesignMode !== 'true') return;
      if (element.dataset.countdownWarned === 'true') return;
      element.dataset.countdownWarned = 'true';
      console.warn(
        '[AKNES Carousel] Invalid countdown ISO date:',
        element.dataset.countdownBlockId || 'unknown block'
      );
    }

    setInvalid(element) {
      element.dataset.countdownInvalid = 'true';
      element.classList.add('aknes-hero-countdown--invalid');
      const editorMessage = element.querySelector('[data-aknes-countdown-editor-message]');
      if (editorMessage) editorMessage.hidden = false;
      this.warnInvalid(element);
    }

    setValue(element, selector, value) {
      const target = element.querySelector(selector);
      if (target) target.textContent = String(value).padStart(2, '0');
    }

    setNumbers(element, difference) {
      const safeDifference = Math.max(0, difference);
      const totalSeconds = Math.floor(safeDifference / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setValue(element, '[data-countdown-days]', days);
      this.setValue(element, '[data-countdown-hours]', hours);
      this.setValue(element, '[data-countdown-minutes]', minutes);
      this.setValue(element, '[data-countdown-seconds]', seconds);
    }

    setState(element, state, label) {
      const slide = element.closest('[data-aknes-hero-slide]');
      const event = element.closest('[data-aknes-countdown-event]');
      const status = slide?.querySelector('[data-aknes-countdown-status]');

      if (slide) {
        slide.classList.remove(...stateClasses);
        slide.classList.add(`aknes-hero-slide--${state}`);
      }

      if (event) event.dataset.countdownState = state;
      if (status) status.textContent = label;
    }

    updateCountdown(element, now) {
      const mode = element.dataset.countdownMode || 'start_then_end';
      const startValue = element.dataset.countdownStart || '';
      const endValue = element.dataset.countdownEnd || '';
      const start = isoWithTimezone.test(startValue) ? Date.parse(startValue) : NaN;
      const end = isoWithTimezone.test(endValue) ? Date.parse(endValue) : NaN;
      const validEnd = Number.isFinite(end);
      const validStart = Number.isFinite(start);
      const requiresStart = mode === 'start_then_end';

      if (!validEnd || (requiresStart && (!validStart || start >= end))) {
        this.setInvalid(element);
        return;
      }

      element.dataset.countdownInvalid = 'false';
      element.classList.remove('aknes-hero-countdown--invalid');
      const editorMessage = element.querySelector('[data-aknes-countdown-editor-message]');
      if (editorMessage) editorMessage.hidden = true;

      let state = 'live';
      let target = end;
      let label = element.dataset.liveLabel || 'Live Now';

      if (requiresStart && now < start) {
        state = 'upcoming';
        target = start;
        label = element.dataset.upcomingLabel || 'Upcoming Event';
      } else if (now >= end) {
        state = 'ended';
        target = now;
        label = element.dataset.endedLabel || 'Ended';
      } else if (end - now <= 86400000) {
        state = 'ending';
        label = element.dataset.endingLabel || 'Ends Soon';
      }

      this.setState(element, state, label);
      this.setNumbers(element, state === 'ended' ? 0 : target - now);

      const hideCountdown = state === 'ended' && element.dataset.countdownAfterEnd === 'hide_countdown';
      element.classList.toggle('aknes-hero-countdown--hidden', hideCountdown);
    }

    update() {
      if (document.hidden) return;
      const now = Date.now();
      this.countdowns.forEach((element) => this.updateCountdown(element, now));
    }
  }

  const init = (root) => {
    if (!(root instanceof Element) || !root.matches('[data-aknes-hero]')) return;
    if (!root.querySelector('[data-aknes-countdown]') || instances.has(root)) return;
    instances.set(root, new CountdownSection(root));
  };

  const destroy = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const initAll = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-aknes-hero]')) init(scope);
    scope.querySelectorAll?.('[data-aknes-hero]').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-aknes-hero]')
      ? event.target
      : event.target.querySelector?.('[data-aknes-hero]');
    if (root) destroy(root);
  });

  window.AknesCarouselCountdownManager = { initAll };
  initAll();
})();
